import { Suspense, lazy, useEffect, useState } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { Analytics } from "@vercel/analytics/react"
import * as Sentry from '@sentry/react'

// --- COMPONENTS ---
const Header = lazy(() => import('./components/Header'))
const Footer = lazy(() => import('./components/Footer'))
const ScrollToTop = lazy(() => import('./components/ScrollToTop'))
const ScrollProgress = lazy(() => import('./components/ScrollProgress'))
const CookieConsentModal = lazy(() => import('./components/CookieConsentModal'))
const MobileActionDock = lazy(() => import('./components/MobileActionDock'))
const SolutionFinderModal = lazy(() => import('./components/SolutionFinderModal.jsx'))

// --- PAGES ---
const Landing = lazy(() => import('./pages/Landing'))
const Home = lazy(() => import('./pages/Home'))
const Lerncoaching = lazy(() => import('./pages/Lerncoaching'))
const Programmieren = lazy(() => import('./pages/Programmieren'))
const Kontakt = lazy(() => import('./pages/Kontakt'))
const Philosophie = lazy(() => import('./pages/Philosophie'))
const Preise = lazy(() => import('./pages/Preise'))
const Studenten = lazy(() => import('./pages/Studenten'))
const Eltern = lazy(() => import('./pages/Eltern'))
const Schueler = lazy(() => import('./pages/Schueler'))
const Bildungswege = lazy(() => import('./pages/Bildungswege'))
const Datenschutz = lazy(() => import('./pages/Datenschutz'))
const Impressum = lazy(() => import('./pages/Impressum'))

export default function App() {
    const { pathname } = useLocation()
    const isLanding = pathname === '/'
    const [deferredComponentsReady, setDeferredComponentsReady] = useState(false)

    useEffect(() => {
        // Signalisiert, dass die App gemountet ist (für verzögertes Laden)
        setDeferredComponentsReady(true)
    }, [])

    useEffect(() => {
        if (typeof document === 'undefined') return

        // Scroll-Verhalten für Landing-Page vs. Rest der App
        if (isLanding) {
            document.body.style.overflow = 'auto'
        } else {
            document.body.style.overflow = ''
        }

        return () => {
            document.body.style.overflow = ''
        }
    }, [isLanding])

    return (
        <Sentry.ErrorBoundary fallback={<p className="p-4 text-center text-red-500">Ein unerwarteter Fehler ist aufgetreten.</p>}>
            <div className="flex min-h-screen flex-col bg-slate-50 transition-colors duration-500 dark:bg-slate-950">
                <Analytics />

                {deferredComponentsReady && (
                    <Suspense fallback={null}>
                        <CookieConsentModal />
                    </Suspense>
                )}

                {deferredComponentsReady && (
                    <Suspense fallback={null}>
                        <ScrollToTop />
                    </Suspense>
                )}

                {/* Header nur anzeigen, wenn NICHT auf Landing Page */}
                {!isLanding && (
                    <Suspense fallback={<div className="h-20 w-full bg-white/50 animate-pulse" />}>
                        <Header />
                    </Suspense>
                )}

                {!isLanding && (
                    <Suspense fallback={null}>
                        <ScrollProgress />
                    </Suspense>
                )}

                <main
                    className={
                        isLanding
                            ? 'flex flex-1 items-center justify-center overflow-x-hidden overflow-y-auto p-0'
                            : 'mx-auto w-full max-w-6xl flex-1 px-4 pb-16 pt-24 md:px-6 md:pt-28 lg:px-8'
                    }
                >
                    <Suspense
                        fallback={
                            <div className="flex flex-1 items-center justify-center py-20">
                                <div className="flex flex-col items-center gap-3 text-slate-500">
                                    {/* Optionaler Lade-Spinner */}
                                    <span className="h-12 w-12 animate-spin rounded-full border-4 border-blue-500/40 border-t-transparent" aria-hidden />
                                    <p className="text-sm font-medium">Lade Inhalte …</p>
                                </div>
                            </div>
                        }
                    >
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/uebersicht" element={<Home />} />

                            {/* Redirects für alte URLs */}
                            <Route path="/übersicht" element={<Navigate to="/uebersicht" replace />} />
                            <Route path="/entdecken" element={<Navigate to="/uebersicht" replace />} />
                            <Route path="/loesung" element={<Navigate to="/uebersicht" replace />} />

                            {/* Hauptseiten */}
                            <Route path="/studenten" element={<Studenten />} />
                            <Route path="/eltern" element={<Eltern />} />
                            <Route path="/schueler" element={<Schueler />} />
                            <Route path="/lerncoaching" element={<Lerncoaching />} />
                            <Route path="/programmieren" element={<Programmieren />} />
                            <Route path="/philosophie" element={<Philosophie />} />
                            <Route path="/preise" element={<Preise />} />
                            <Route path="/kontakt" element={<Kontakt />} />
                            <Route path="/bildungswege" element={<Bildungswege />} />

                            {/* Rechtliches */}
                            <Route path="/datenschutz" element={<Datenschutz />} />
                            <Route path="/impressum" element={<Impressum />} />

                            {/* 404 Fallback */}
                            <Route path="*" element={<div className="p-10 text-center text-slate-500">Seite nicht gefunden (404)</div>} />
                        </Routes>
                    </Suspense>
                </main>

                {/* Footer nur anzeigen, wenn NICHT auf Landing Page */}
                {!isLanding && (
                    <Suspense fallback={null}>
                        <Footer />
                    </Suspense>
                )}

                {/* Mobile Dock nur anzeigen, wenn NICHT auf Landing Page */}
                {deferredComponentsReady && !isLanding && (
                    <Suspense fallback={null}>
                        <MobileActionDock />
                    </Suspense>
                )}

                {/* Lösungsfinder Modal immer verfügbar */}
                <Suspense fallback={null}>
                    <SolutionFinderModal />
                </Suspense>
            </div>
        </Sentry.ErrorBoundary>
    )
}